import { makeAutoObservable, runInAction } from 'mobx'

import { notificationService, restClient } from '@/Core'
import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'
import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'
import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { APPLICATION_TEXTS as T } from '../Contracts/ApplicationFieldLabels'
import { APPLICATION_LOSS_REASON_RESOURCE, APPLICATION_STATUS_CODES } from '../Contracts/IApplication'
import { PatchApplicationTask, RunApplicationTransitionTask } from '../Tasks/RunApplicationActionTask'

import type { LossReasonOption } from '@/Containers/TransportationSection/Components'
import type { ApplicationCrudContextLike, ApplicationPermissions } from '../Contracts/IApplication'
import type { ApplicationTransition } from '../Tasks/RunApplicationActionTask'

/** Модалки картки (`SPEC-021` §8.6); «Програти» — спільна `LossReasonModal`. */
export type ApplicationModal = 'statusClient' | 'reportProblem' | 'resolveProblem' | 'unload' | 'lose'

/** Поле тіла запиту кожної модалки — під ним показується 422. */
const MODAL_FIELDS: Record<ApplicationModal, string> = {
    statusClient: 'statusClient',
    reportProblem: 'description',
    resolveProblem: 'description',
    unload: 'dateUnloading',
    lose: 'lossReason',
}

/** Найдовший текст опису проблеми й коментаря (`ApplicationEntity::TEXT_MAX_LENGTH`). */
export const APPLICATION_TEXT_MAX_LENGTH = 4096

/** Найдовший «Статус для клієнта» (`ApplicationEntity::STRING_MAX_LENGTH`). */
export const APPLICATION_STRING_MAX_LENGTH = 255

/**
 * Статуси, після яких картка вже не та, що на екрані: 400 — переходу з
 * поточного стану вже немає, 403 — права змінилися, 404 — заявку видалено.
 */
const STALE_CARD_STATUSES = new Set([400, 403, 404])

interface Violation {
    field?: string
    propertyPath?: string
    message?: string
}

const violationsOf = (error: unknown): Violation[] => {
    const list = (error as { response?: { violations?: unknown } } | null)?.response?.violations

    return Array.isArray(list) ? (list as Violation[]) : []
}

interface LossReasonRow {
    uuid?: string
    '@id'?: string
    title?: string
}

/**
 * Дії картки заявки (`SPEC-021` §8.4–§8.6): кнопки шапки й «⋮», модалки,
 * «Програти» біля смуги і клік на крок смуги статусів.
 *
 * Видимість — **лише** `_permissions` запису (легасі питав ролі на фронті,
 * `useButtons4DrawerExtra.jsx:106-138`). Після успіху картка бере нову версію з
 * відповіді (статус, `_permissions`), а список під шухлядою перечитується —
 * разом із ним і плашки статусів.
 */
export class ApplicationActionsStore {
    modal: ApplicationModal | null = null
    pending = false

    text = ''
    date: string | null = null
    lossReason: string | undefined = undefined
    lossComment = ''
    fieldError: string | null = null
    error: string | null = null

    reasons: LossReasonOption[] = []
    reasonsLoading = false

    /** Код кроку смуги, для якого зараз питаємо вимоги */

    constructor(private crudContext: ApplicationCrudContextLike) {
        makeAutoObservable<ApplicationActionsStore, 'crudContext'>(this, { crudContext: false })
    }

    /** Ядро створює `crudContext` картки наново — стор лишається, контекст оновлюється. */
    bind(crudContext: ApplicationCrudContextLike): void {
        this.crudContext = crudContext
    }

    get values(): Record<string, unknown> {
        return this.crudContext.formContext?.values ?? {}
    }

    get permissions(): ApplicationPermissions {
        const permissions = this.crudContext.formContext?.values?._permissions

        return permissions !== null && typeof permissions === 'object' ? permissions : {}
    }

    get uuid(): string | null {
        const uuid = this.crudContext.formContext?.values?.uuid

        return typeof uuid === 'string' && uuid !== '' ? uuid : null
    }

    open(modal: ApplicationModal): void {
        if (this.pending) return

        this.modal = modal
        this.fieldError = null
        this.error = null
        this.lossReason = undefined
        this.lossComment = ''
        this.date = null
        this.text = modal === 'statusClient' ? String(this.values['statusClient'] ?? '') : ''

        if (modal === 'lose' && this.reasons.length === 0) void this.loadReasons()
    }

    close(): void {
        this.modal = null
        this.fieldError = null
        this.error = null
    }

    setText(value: string): void {
        this.text = value
        this.fieldError = null
    }

    setDate(value: string | null): void {
        this.date = value
        this.fieldError = null
    }

    setLossReason(value: string | undefined): void {
        this.lossReason = value
        this.fieldError = null
    }

    setLossComment(value: string): void {
        this.lossComment = value
    }

    /**
     * «Зберегти» модалки. Обов'язковість поля перевіряє бек (422 під полем);
     * порожнє «Статус для клієнта» і дату ловимо тут — бек прийняв би `null`
     * як «стерти», а легасі вимагав значення (`useButtons4DrawerExtra.jsx:20-96`).
     */
    async submit(): Promise<void> {
        const modal = this.modal
        const uuid = this.uuid

        if (!modal || !uuid || this.pending) return

        if (modal === 'statusClient' && this.text.trim() === '') {
            this.fieldError = T.required
            return
        }

        if (modal === 'unload' && !this.date) {
            this.fieldError = T.required
            return
        }

        this.pending = true

        try {
            const card = await this.request(modal, uuid)

            runInAction(() => {
                this.pending = false
                this.modal = null
            })

            notificationService.showSuccess(T.successTitle, SUCCESS[modal])
            await this.applyCard(card)
            this.reloadList()
        } catch (error: unknown) {
            runInAction(() => {
                this.pending = false
            })

            this.handleError(modal, error)
        }
    }

    private async request(modal: ApplicationModal, uuid: string): Promise<Record<string, unknown> | null> {
        if (modal === 'statusClient') return PatchApplicationTask(uuid, { statusClient: this.text.trim() })
        if (modal === 'unload') return PatchApplicationTask(uuid, { dateUnloading: this.date })

        const transition: ApplicationTransition =
            modal === 'lose' ? 'lose' : modal === 'reportProblem' ? 'report_problem' : 'resolve_problem'

        const body: Record<string, unknown> =
            modal === 'lose'
                ? {
                      lossReason: this.lossReason ?? null,
                      ...(this.lossComment.trim() !== '' ? { comment: this.lossComment.trim() } : {}),
                  }
                : { description: this.text.trim() === '' ? null : this.text.trim() }

        return RunApplicationTransitionTask(uuid, transition, body)
    }

    private handleError(modal: ApplicationModal, error: unknown): void {
        const status = serverStatus(error)

        if (status === 422) {
            const field = MODAL_FIELDS[modal]
            const violation = violationsOf(error).find(
                (item) => (item.field ?? item.propertyPath) === field && typeof item.message === 'string'
            )

            if (violation?.message) {
                this.fieldError = violation.message
                return
            }
        }

        const message = serverMessage(error, T.contactAdmin)
        const isStale = status !== null && STALE_CARD_STATUSES.has(status)

        if (!isStale) {
            this.error = message
            return
        }

        this.close()
        notificationService.showError(T.errorTitle, message)
        void this.reloadCard()
        this.reloadList()
    }

    private async loadReasons(): Promise<void> {
        const restUrl = GetResourceTask(APPLICATION_LOSS_REASON_RESOURCE)?.restUrl

        if (typeof restUrl !== 'string' || restUrl === '') return

        this.reasonsLoading = true

        try {
            const raw = (await restClient.get(`${restUrl}?limit=100`, undefined, { forceRefresh: true })) as unknown
            const data = raw as { items?: LossReasonRow[] } | null
            const rows = data?.items ?? []

            runInAction(() => {
                this.reasons = rows
                    .filter((row): row is LossReasonRow & { uuid: string } => typeof row.uuid === 'string')
                    .map((row) => ({
                        value: typeof row['@id'] === 'string' ? row['@id'] : row.uuid,
                        label: typeof row.title === 'string' ? row.title : row.uuid,
                    }))
            })
        } catch {
            // Без переліку модалка лишається з порожнім вибором — бек відповість 422
        } finally {
            runInAction(() => {
                this.reasonsLoading = false
            })
        }
    }

    private async applyCard(card: Record<string, unknown> | null): Promise<void> {
        const form = this.crudContext.formContext
        const permissions = card?.['_permissions']

        if (card && permissions !== null && typeof permissions === 'object' && form?.setInitialValues) {
            form.setInitialValues(card)
            form.setStorage?.('responseData', { ...card })
            return
        }

        await this.reloadCard()
    }

    private async reloadCard(): Promise<void> {
        try {
            await this.crudContext.actions?.reloadData?.()
        } catch {
            // Запис могли видалити — лишається повідомлення сервера, показане вище
        }
    }

    reloadList(): void {
        const load = this.crudContext.listCrudContext?.listContext?.actions?.load

        if (typeof load === 'function') {
            load(true)
            return
        }

        const key = this.crudContext.resource?.key

        if (typeof key === 'string' && key !== '') crudResourceRegistry.reloadListIfExists(key)
    }
}

/** Повідомлення після успіху (`SPEC-021` §8.6, `uk.json`). */
const SUCCESS: Record<ApplicationModal, string> = {
    statusClient: T.updateStatusSuccess,
    reportProblem: T.saved,
    resolveProblem: T.resolveProblemSuccess,
    unload: T.saved,
    lose: T.loseSuccess,
}

/** Чи заявка в кінцевому стані «Програш» — тоді «Програти» й крок смуги не клікаються. */
export const isLostApplication = (values: Record<string, unknown> | undefined): boolean =>
    (values?.['status'] as { code?: unknown } | null | undefined)?.code === APPLICATION_STATUS_CODES.loss

const stores = new WeakMap<object, ApplicationActionsStore>()

/**
 * Стор дій картки — один на ядровий `crudContext`: кнопки (`onButtons`),
 * модалки (`content`) і смуга статусів у шаблоні беруть той самий екземпляр.
 */
export const applicationActionsStoreFor = (crudContext: ApplicationCrudContextLike): ApplicationActionsStore => {
    let store = stores.get(crudContext)

    if (!store) {
        store = new ApplicationActionsStore(crudContext)
        stores.set(crudContext, store)
    } else {
        store.bind(crudContext)
    }

    return store
}
